/**
 * readDroppedEntries — extrae todos los ficheros de un DataTransferItemList,
 * recorriendo carpetas (y subcarpetas) recursivamente. Permite soltar una carpeta
 * entera de documentos en el chat de ARIA / la Bandeja, no solo ficheros sueltos.
 *
 * IMPORTANTE: debe invocarse de forma SÍNCRONA dentro del handler `onDrop`. Los
 * `DataTransferItem` y sus entries solo son válidos durante el evento de drop;
 * esta función captura los entries en su primer tick (antes de cualquier `await`)
 * y luego recorre las carpetas de forma asíncrona sobre esos entries ya capturados.
 *
 * El FILTRADO por tipo/tamaño lo hace el consumidor; aquí solo se aplanan los
 * ficheros. Para el chat de ARIA, con `partitionFiles` (`lib/ariaAcceptedFiles`),
 * que decide con los mismos predicados con los que luego se rutea el fichero.
 *
 * Lo que NO vale es comparar `file.type` contra una lista de MIME: el navegador no
 * siempre lo reporta —un `.csv`, un `.heic`— y eso anula el fallback por extensión
 * que los predicados ya tienen. Este comentario llegó a nombrar las dos formas como
 * si fueran equivalentes, y no lo son: era el bug.
 *
 * @param {DataTransferItemList|DataTransferItem[]} items
 * @returns {Promise<File[]>}
 */
export async function readDroppedEntries(items) {
  // Paso síncrono: capturar entries/ficheros antes de que el navegador vacíe la
  // lista al terminar el handler.
  const entries = [];
  const looseFiles = [];
  for (const item of Array.from(items || [])) {
    if (item.kind && item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
    } else {
      const f = item.getAsFile?.();
      if (f) looseFiles.push(f);
    }
  }

  const out = [...looseFiles];
  for (const entry of entries) {
    await walkEntry(entry, out);
  }
  return out;
}

/**
 * extractDroppedFiles — normaliza el `dataTransfer` de un evento de drop a una
 * lista plana de `File`, recorriendo carpetas si el navegador expone entries.
 *
 * DEBE llamarse de forma SÍNCRONA dentro del handler `onDrop` (ver readDroppedEntries):
 * su primer paso captura los entries antes de que el navegador vacíe la lista.
 *
 * @param {DataTransfer} dataTransfer
 * @returns {Promise<File[]>}
 */
export function extractDroppedFiles(dataTransfer) {
  const items = dataTransfer?.items;
  if (items?.length && typeof items[0]?.webkitGetAsEntry === 'function') {
    return readDroppedEntries(items);
  }
  return Promise.resolve(Array.from(dataTransfer?.files || []));
}

// Recorre un FileSystemEntry: si es fichero lo añade; si es carpeta, lee sus
// hijos por lotes (readEntries pagina, hay que llamarlo hasta devolver []).
function walkEntry(entry, out) {
  return new Promise((resolve) => {
    if (!entry) { resolve(); return; }
    if (entry.isFile) {
      entry.file(
        (file) => { out.push(file); resolve(); },
        () => resolve(), // ignora ficheros ilegibles en vez de abortar la tanda
      );
      return;
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const readBatch = () => {
        reader.readEntries(
          async (batch) => {
            if (!batch || !batch.length) { resolve(); return; }
            for (const child of batch) {
              await walkEntry(child, out);
            }
            readBatch(); // seguir hasta que un lote venga vacío
          },
          () => resolve(),
        );
      };
      readBatch();
      return;
    }
    resolve();
  });
}
